// frontend/src/context/AuthContext.jsx - FIXED for Team Members
import React, { createContext, useState, useEffect, useContext } from "react";
import API, {
  login as loginAPI,
  register as registerAPI,
  getMe,
  refreshToken as refreshTokenAPI,
  logout as logoutAPI,
} from "../api";
import {
  faChartLine,
  faShoppingCart,
  faUserCircle,
  faCreditCard,
  faQuoteLeft,
  faClipboardList,
  faTruck,
  faBox,
  faUserTie,
  faUsers,
  faRobot,
  faTractor,
  faStore,
  faHandshake,
  faLightbulb,
  faBriefcase,
  faCode,
  faShieldAlt,
  faCrown,
  faUserCog,
  faDollarSign,
  faCalendarCheck,
  faFileInvoice,
  faWarehouse,
  faShippingFast,
  faChartPie,
  faBell,
  faEnvelope,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// ============================================
// TEAM ROLES CONSTANTS
// ============================================
export const TEAM_ROLES = {
  SALE_REPRESENTATIVE: "sale_representative",
  ADVISOR: "advisor",
  COORDINATOR: "coordinator",
  ASSISTANT_ADVISOR: "assistant_advisor",
  ASSISTANT_COORDINATOR: "assistant_coordinator",
  MANAGER: "manager",
  ASSISTANT_MANAGER: "assistant_manager",
  ADMINISTRATOR: "administrator",
  COMMUNITY_RELATION: "community_relation",
  DEVELOPER: "developer",
};

export const TEAM_ROLE_LABELS = {
  [TEAM_ROLES.SALE_REPRESENTATIVE]: "Sales Representative",
  [TEAM_ROLES.ADVISOR]: "Advisor",
  [TEAM_ROLES.COORDINATOR]: "Coordinator",
  [TEAM_ROLES.ASSISTANT_ADVISOR]: "Assistant Advisor",
  [TEAM_ROLES.ASSISTANT_COORDINATOR]: "Assistant Coordinator",
  [TEAM_ROLES.MANAGER]: "Manager",
  [TEAM_ROLES.ASSISTANT_MANAGER]: "Assistant Manager",
  [TEAM_ROLES.ADMINISTRATOR]: "Administrator",
  [TEAM_ROLES.COMMUNITY_RELATION]: "Community & Farmer Relation",
  [TEAM_ROLES.DEVELOPER]: "Developer",
};

export const TEAM_ROLE_ICONS = {
  [TEAM_ROLES.SALE_REPRESENTATIVE]: faHandshake,
  [TEAM_ROLES.ADVISOR]: faLightbulb,
  [TEAM_ROLES.COORDINATOR]: faClipboardList,
  [TEAM_ROLES.ASSISTANT_ADVISOR]: faUserTie,
  [TEAM_ROLES.ASSISTANT_COORDINATOR]: faUserCog,
  [TEAM_ROLES.MANAGER]: faBriefcase,
  [TEAM_ROLES.ASSISTANT_MANAGER]: faUserTie,
  [TEAM_ROLES.ADMINISTRATOR]: faShieldAlt,
  [TEAM_ROLES.COMMUNITY_RELATION]: faUsers,
  [TEAM_ROLES.DEVELOPER]: faCode,
};

// ============================================
// DASHBOARD CONFIGURATION BY ROLE
// ============================================
export const DASHBOARD_CONFIG = {
  // ========== SUPER ADMIN ==========
  super_admin: {
    label: "Super Admin Dashboard",
    icon: faCrown,
    color: "#D9A321",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders-all", label: "All Orders", icon: faShoppingCart },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "shipments", label: "Shipments", icon: faTruck },
      { id: "products", label: "Products", icon: faBox },
      { id: "users", label: "Users", icon: faUsers },
      { id: "team", label: "Team", icon: faUserTie },
      { id: "analytics", label: "Analytics", icon: faChartPie },
      { id: "ai-analytics", label: "AI Analytics", icon: faRobot },
      { id: "payments", label: "Payments", icon: faCreditCard },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: [
      "totalProducts",
      "totalOrders",
      "totalUsers",
      "totalRevenue",
      "activeShipments",
    ],
  },

  // ========== ADMIN ==========
  admin: {
    label: "Admin Dashboard",
    icon: faShieldAlt,
    color: "#3b82f6",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders-all", label: "All Orders", icon: faShoppingCart },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "shipments", label: "Shipments", icon: faTruck },
      { id: "products", label: "Products", icon: faBox },
      { id: "users", label: "Users", icon: faUsers },
      { id: "team", label: "Team", icon: faUserTie },
      { id: "analytics", label: "Analytics", icon: faChartPie },
      { id: "payments", label: "Payments", icon: faCreditCard },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: [
      "totalProducts",
      "totalOrders",
      "totalUsers",
      "totalRevenue",
      "activeShipments",
    ],
  },

  // ========== SALE REPRESENTATIVE ==========
  [TEAM_ROLES.SALE_REPRESENTATIVE]: {
    label: "Sales Dashboard",
    icon: faHandshake,
    color: "#3b82f6",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders", label: "My Orders", icon: faShoppingCart },
      { id: "products", label: "Products", icon: faBox },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "shipments", label: "Shipments", icon: faTruck },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["totalOrders", "totalItems", "totalSpent"],
  },

  // ========== ADVISOR ==========
  [TEAM_ROLES.ADVISOR]: {
    label: "Advisor Dashboard",
    icon: faLightbulb,
    color: "#f59e0b",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders", label: "My Orders", icon: faShoppingCart },
      { id: "products", label: "Products", icon: faBox },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "analytics", label: "Analytics", icon: faChartPie },
      { id: "testimonials", label: "Testimonials", icon: faQuoteLeft },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["totalOrders", "totalItems", "totalSpent", "preOrders"],
  },

  // ========== COORDINATOR ==========
  [TEAM_ROLES.COORDINATOR]: {
    label: "Coordinator Dashboard",
    icon: faClipboardList,
    color: "#8b5cf6",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders-all", label: "All Orders", icon: faShoppingCart },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "shipments", label: "Shipments", icon: faTruck },
      { id: "products", label: "Products", icon: faBox },
      { id: "analytics", label: "Analytics", icon: faChartPie },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["totalOrders", "activeShipments", "preOrders", "totalRevenue"],
  },

  // ========== ASSISTANT ADVISOR ==========
  [TEAM_ROLES.ASSISTANT_ADVISOR]: {
    label: "Assistant Advisor Dashboard",
    icon: faUserTie,
    color: "#06b6d4",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders", label: "My Orders", icon: faShoppingCart },
      { id: "products", label: "Products", icon: faBox },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["totalOrders", "totalItems", "preOrders"],
  },

  // ========== ASSISTANT COORDINATOR ==========
  [TEAM_ROLES.ASSISTANT_COORDINATOR]: {
    label: "Assistant Coordinator Dashboard",
    icon: faUserCog,
    color: "#ec4899",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders", label: "My Orders", icon: faShoppingCart },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "shipments", label: "Shipments", icon: faTruck },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["totalOrders", "activeShipments", "preOrders"],
  },

  // ========== MANAGER ==========
  [TEAM_ROLES.MANAGER]: {
    label: "Manager Dashboard",
    icon: faBriefcase,
    color: "#14b8a6",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders-all", label: "All Orders", icon: faShoppingCart },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "shipments", label: "Shipments", icon: faTruck },
      { id: "products", label: "Products", icon: faBox },
      { id: "team", label: "Team", icon: faUserTie },
      { id: "analytics", label: "Analytics", icon: faChartPie },
      { id: "payments", label: "Payments", icon: faCreditCard },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: [
      "totalOrders",
      "totalRevenue",
      "activeShipments",
      "totalUsers",
      "totalProducts",
    ],
  },

  // ========== ASSISTANT MANAGER ==========
  [TEAM_ROLES.ASSISTANT_MANAGER]: {
    label: "Assistant Manager Dashboard",
    icon: faUserTie,
    color: "#f97316",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders-all", label: "All Orders", icon: faShoppingCart },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "shipments", label: "Shipments", icon: faTruck },
      { id: "products", label: "Products", icon: faBox },
      { id: "analytics", label: "Analytics", icon: faChartPie },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["totalOrders", "activeShipments", "totalRevenue", "preOrders"],
  },

  // ========== ADMINISTRATOR ==========
  [TEAM_ROLES.ADMINISTRATOR]: {
    label: "Administrator Dashboard",
    icon: faShieldAlt,
    color: "#8b5cf6",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders-all", label: "All Orders", icon: faShoppingCart },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "shipments", label: "Shipments", icon: faTruck },
      { id: "products", label: "Products", icon: faBox },
      { id: "users", label: "Users", icon: faUsers },
      { id: "team", label: "Team", icon: faUserTie },
      { id: "analytics", label: "Analytics", icon: faChartPie },
      { id: "payments", label: "Payments", icon: faCreditCard },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: [
      "totalProducts",
      "totalOrders",
      "totalUsers",
      "totalRevenue",
      "activeShipments",
    ],
  },

  // ========== COMMUNITY RELATION ==========
  [TEAM_ROLES.COMMUNITY_RELATION]: {
    label: "Community Relations Dashboard",
    icon: faUsers,
    color: "#22c55e",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "testimonials", label: "Testimonials", icon: faQuoteLeft },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["testimonials", "communityEngagement"],
  },

  // ========== DEVELOPER ==========
  [TEAM_ROLES.DEVELOPER]: {
    label: "Developer Dashboard",
    icon: faCode,
    color: "#6366f1",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "analytics", label: "Analytics", icon: faChartPie },
      { id: "ai-analytics", label: "AI Analytics", icon: faRobot },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["systemStatus", "apiCalls", "errorRate"],
  },

  // ========== FARMER ==========
  farmer: {
    label: "Farmer Dashboard",
    icon: faTractor,
    color: "#16a34a",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders", label: "My Orders", icon: faShoppingCart },
      { id: "preorders", label: "Pre-Orders", icon: faCalendarCheck },
      { id: "payments", label: "Payments", icon: faCreditCard },
      { id: "testimonials", label: "Testimonials", icon: faQuoteLeft },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["totalOrders", "bulkOrders", "totalSpent", "preOrders"],
  },

  // ========== SUPPLIER ==========
  supplier: {
    label: "Supplier Dashboard",
    icon: faStore,
    color: "#2563eb",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "supplier-products", label: "My Products", icon: faBox },
      { id: "supplier-orders", label: "Orders", icon: faShoppingCart },
      { id: "supplier-shipments", label: "Shipments", icon: faTruck },
      { id: "payments", label: "Payments", icon: faCreditCard },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["totalProducts", "totalOrders", "totalRevenue"],
  },

  // ========== CUSTOMER ==========
  customer: {
    label: "My Account",
    icon: faStore,
    color: "#6b7280",
    tabs: [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders", label: "My Orders", icon: faShoppingCart },
      { id: "payments", label: "Payments", icon: faCreditCard },
      { id: "testimonials", label: "Testimonials", icon: faQuoteLeft },
      { id: "profile", label: "Profile", icon: faUserCircle },
    ],
    stats: ["totalOrders", "totalItems", "totalSpent"],
  },
};

// ============================================
// ROLE PERMISSIONS MATRIX
// ============================================
export const ROLE_PERMISSIONS = {
  super_admin: {
    canViewAll: true,
    canManageUsers: true,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: true,
    canManageTeam: true,
    canManageAnalytics: true,
    canManagePayments: true,
    canManagePreOrders: true,
    canManageTestimonials: true,
    canViewRevenue: true,
    canExportData: true,
    canManageSettings: true,
    canApproveTeam: true,
    canRejectTeam: true,
    canDeleteUsers: true,
    canManageFarmers: true,
    canManageSuppliers: true,
  },
  admin: {
    canViewAll: true,
    canManageUsers: true,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: true,
    canManageTeam: true,
    canManageAnalytics: true,
    canManagePayments: true,
    canManagePreOrders: true,
    canManageTestimonials: true,
    canViewRevenue: true,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: true,
    canRejectTeam: true,
    canDeleteUsers: false,
    canManageFarmers: true,
    canManageSuppliers: true,
  },
  sale_representative: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: true,
    canManageTeam: false,
    canManageAnalytics: false,
    canManagePayments: false,
    canManagePreOrders: true,
    canManageTestimonials: false,
    canViewRevenue: false,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
  advisor: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: true,
    canManageTeam: false,
    canManageAnalytics: true,
    canManagePayments: false,
    canManagePreOrders: true,
    canManageTestimonials: true,
    canViewRevenue: true,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: true,
    canManageSuppliers: false,
  },
  coordinator: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: true,
    canManageTeam: false,
    canManageAnalytics: true,
    canManagePayments: false,
    canManagePreOrders: true,
    canManageTestimonials: true,
    canViewRevenue: true,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
  assistant_advisor: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: false,
    canManageTeam: false,
    canManageAnalytics: false,
    canManagePayments: false,
    canManagePreOrders: true,
    canManageTestimonials: false,
    canViewRevenue: false,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
  assistant_coordinator: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: false,
    canManageTeam: false,
    canManageAnalytics: false,
    canManagePayments: false,
    canManagePreOrders: true,
    canManageTestimonials: false,
    canViewRevenue: false,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
  manager: {
    canViewAll: false,
    canManageUsers: true,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: true,
    canManageTeam: true,
    canManageAnalytics: true,
    canManagePayments: true,
    canManagePreOrders: true,
    canManageTestimonials: true,
    canViewRevenue: true,
    canExportData: true,
    canManageSettings: false,
    canApproveTeam: true,
    canRejectTeam: true,
    canDeleteUsers: false,
    canManageFarmers: true,
    canManageSuppliers: true,
  },
  assistant_manager: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: true,
    canManageTeam: false,
    canManageAnalytics: true,
    canManagePayments: false,
    canManagePreOrders: true,
    canManageTestimonials: true,
    canViewRevenue: true,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
  administrator: {
    canViewAll: true,
    canManageUsers: true,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: true,
    canManageTeam: true,
    canManageAnalytics: true,
    canManagePayments: true,
    canManagePreOrders: true,
    canManageTestimonials: true,
    canViewRevenue: true,
    canExportData: true,
    canManageSettings: true,
    canApproveTeam: true,
    canRejectTeam: true,
    canDeleteUsers: true,
    canManageFarmers: true,
    canManageSuppliers: true,
  },
  community_relation: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: false,
    canManageOrders: false,
    canManageShipments: false,
    canManageTeam: false,
    canManageAnalytics: false,
    canManagePayments: false,
    canManagePreOrders: false,
    canManageTestimonials: true,
    canViewRevenue: false,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
  developer: {
    canViewAll: true,
    canManageUsers: false,
    canManageProducts: false,
    canManageOrders: false,
    canManageShipments: false,
    canManageTeam: false,
    canManageAnalytics: true,
    canManagePayments: false,
    canManagePreOrders: false,
    canManageTestimonials: false,
    canViewRevenue: true,
    canExportData: true,
    canManageSettings: true,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
  farmer: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: false,
    canManageOrders: true,
    canManageShipments: false,
    canManageTeam: false,
    canManageAnalytics: false,
    canManagePayments: true,
    canManagePreOrders: true,
    canManageTestimonials: true,
    canViewRevenue: false,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
  customer: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: false,
    canManageOrders: true,
    canManageShipments: false,
    canManageTeam: false,
    canManageAnalytics: false,
    canManagePayments: true,
    canManagePreOrders: true,
    canManageTestimonials: true,
    canViewRevenue: false,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
  supplier: {
    canViewAll: false,
    canManageUsers: false,
    canManageProducts: true,
    canManageOrders: true,
    canManageShipments: true,
    canManageTeam: false,
    canManageAnalytics: false,
    canManagePayments: true,
    canManagePreOrders: false,
    canManageTestimonials: false,
    canViewRevenue: false,
    canExportData: false,
    canManageSettings: false,
    canApproveTeam: false,
    canRejectTeam: false,
    canDeleteUsers: false,
    canManageFarmers: false,
    canManageSuppliers: false,
  },
};

// ============================================
// AUTH PROVIDER
// ============================================
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ============================================
  // ROLE CHECK FUNCTIONS - FIXED FOR TEAM MEMBERS
  // ============================================
  const getEffectiveRole = () => {
    if (!user) return null;
    
    // Super Admin takes priority
    if (user.role === "super_admin") return "super_admin";
    if (user.role === "admin") return "admin";
    
    // Team members with roles - FIXED: check teamRole directly
    if (user.isTeamMember && user.teamRole) {
      // Map team role to the correct key
      const roleMap = {
        "sale_representative": TEAM_ROLES.SALE_REPRESENTATIVE,
        "advisor": TEAM_ROLES.ADVISOR,
        "coordinator": TEAM_ROLES.COORDINATOR,
        "assistant_advisor": TEAM_ROLES.ASSISTANT_ADVISOR,
        "assistant_coordinator": TEAM_ROLES.ASSISTANT_COORDINATOR,
        "manager": TEAM_ROLES.MANAGER,
        "assistant_manager": TEAM_ROLES.ASSISTANT_MANAGER,
        "administrator": TEAM_ROLES.ADMINISTRATOR,
        "community_relation": TEAM_ROLES.COMMUNITY_RELATION,
        "developer": TEAM_ROLES.DEVELOPER,
      };
      return roleMap[user.teamRole] || user.teamRole;
    }
    
    // Regular user types
    return user.userType || user.role || "customer";
  };

  const getEffectiveRoleLabel = () => {
    const role = getEffectiveRole();
    // Check if it's a team role first
    if (role && TEAM_ROLE_LABELS[role]) {
      return TEAM_ROLE_LABELS[role];
    }
    const config = DASHBOARD_CONFIG[role];
    return config?.label || role || "User";
  };

  const getRoleIcon = () => {
    const role = getEffectiveRole();
    // Check if it's a team role first
    if (role && TEAM_ROLE_ICONS[role]) {
      return TEAM_ROLE_ICONS[role];
    }
    const config = DASHBOARD_CONFIG[role];
    return config?.icon || faUserCircle;
  };

  const getRoleColor = () => {
    const role = getEffectiveRole();
    const config = DASHBOARD_CONFIG[role];
    return config?.color || "#5A5A5A";
  };

  const hasPermission = (permission) => {
    const role = getEffectiveRole();
    if (!role) return false;
    if (role === "super_admin") return true;
    if (role === "admin") {
      if (permission === "canDeleteUsers") return false;
      if (permission === "canManageSettings") return false;
      if (permission === "canExportData") return false;
      return true;
    }
    const permissions = ROLE_PERMISSIONS[role];
    if (!permissions) return false;
    return permissions[permission] || false;
  };

  const isSuperAdmin = () => user?.role === "super_admin";
  const isAdmin = () => user?.role === "admin" || user?.role === "super_admin";
  const isTeamMember = () => user?.isTeamMember === true || user?.role === "team";
  const isStaff = () => isAdmin() || isTeamMember();
  const isFarmer = () => user?.userType === "farmer" || user?.role === "farmer";
  const isCustomer = () =>
    user?.userType === "customer" || user?.role === "customer";
  const isSupplier = () =>
    user?.userType === "supplier" || user?.role === "supplier";
  const isPendingApproval = () =>
    user?.isTeamMember === true && user?.isApproved === false;

  const getTeamRoleLabel = (role) => {
    return TEAM_ROLE_LABELS[role] || role;
  };

  const getDashboardConfig = () => {
    const role = getEffectiveRole();
    // For team members, use their specific team role config
    if (role && DASHBOARD_CONFIG[role]) {
      return DASHBOARD_CONFIG[role];
    }
    // Fallback to customer
    return DASHBOARD_CONFIG.customer;
  };

  const getDashboardTabs = () => {
    const config = getDashboardConfig();
    return config.tabs || DASHBOARD_CONFIG.customer.tabs;
  };

  const getDashboardStats = () => {
    const config = getDashboardConfig();
    return config.stats || DASHBOARD_CONFIG.customer.stats;
  };

  // ============================================
  // INITIALIZE AUTH
  // ============================================
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          try {
            const { data } = await getMe();
            // Merge with stored user, keeping token
            setUser({ ...parsed, ...data, token: parsed.token });
          } catch (error) {
            if (error.response?.status === 401) {
              try {
                const refreshResult = await refreshTokenAPI();
                if (refreshResult.data.token) {
                  const { data } = await getMe();
                  const mergedUser = {
                    ...parsed,
                    ...data,
                    token: refreshResult.data.token,
                  };
                  setUser(mergedUser);
                  localStorage.setItem("user", JSON.stringify(mergedUser));
                }
              } catch (refreshError) {
                localStorage.removeItem("user");
                setUser(null);
              }
            } else {
              throw error;
            }
          }
        } catch (error) {
          localStorage.removeItem("user");
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // ============================================
  // LOGIN
  // ============================================
  const login = async (email, password) => {
    try {
      const response = await loginAPI(email, password);
      const userData = response.data;

      if (userData && userData.token) {
        const safeUserData = {
          _id: userData._id,
          name: userData.name,
          email: userData.email,
          role: userData.role || "customer",
          userType: userData.userType || "customer",
          phone: userData.phone || "",
          profileImage: userData.profileImage || "",
          avatar: userData.avatar || "",
          farmDetails: userData.farmDetails,
          bulkPurchaseEnabled: userData.bulkPurchaseEnabled,
          addresses: userData.addresses || [],
          isActive: userData.isActive,
          isVerified: userData.isVerified,
          supplierId: userData.supplierId || null,
          supplier: userData.supplier || null,
          isTeamMember: userData.isTeamMember || false,
          isApproved:
            userData.isApproved !== undefined ? userData.isApproved : true,
          teamRole: userData.teamRole || "",
          teamRoleDisplay: userData.teamRoleDisplay || "",
          bio: userData.bio || "",
          token: userData.token,
          refreshToken: userData.refreshToken,
          canLogin: userData.canLogin !== undefined ? userData.canLogin : true,
          loginMessage: userData.loginMessage || "",
        };
        localStorage.setItem("user", JSON.stringify(safeUserData));
        setUser(safeUserData);
        return safeUserData;
      }
      throw new Error("Invalid response from server - no token received");
    } catch (error) {
      console.error("Login error:", error.response?.data || error.message);
      throw error;
    }
  };

  // ============================================
  // REGISTER
  // ============================================
  const register = async (userData) => {
    try {
      const { data } = await registerAPI(userData);

      const isPending = data.isTeamMember === true && data.isApproved === false;

      const safeUserData = {
        _id: data._id,
        name: data.name,
        email: data.email,
        phone: data.phone || "",
        userType: data.userType || "customer",
        role: data.role || "customer",
        profileImage: data.profileImage || "",
        avatar: data.avatar || "",
        farmDetails: data.farmDetails,
        bulkPurchaseEnabled: data.bulkPurchaseEnabled,
        addresses: data.addresses || [],
        isActive: data.isActive !== undefined ? data.isActive : true,
        isVerified: data.isVerified !== undefined ? data.isVerified : false,
        supplierId: data.supplierId || null,
        isTeamMember: data.isTeamMember || false,
        isApproved: data.isApproved !== undefined ? data.isApproved : true,
        teamRole: data.teamRole || "",
        teamRoleDisplay: data.teamRoleDisplay || "",
        bio: data.bio || "",
        token: data.token || null,
        refreshToken: data.refreshToken || null,
        canLogin: data.canLogin !== undefined ? data.canLogin : !isPending,
        loginMessage: data.loginMessage || "",
        message: data.message || "",
      };

      if (safeUserData.canLogin && safeUserData.token) {
        localStorage.setItem("user", JSON.stringify(safeUserData));
        setUser(safeUserData);
      } else if (isPending) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...safeUserData,
            token: null,
            refreshToken: null,
          }),
        );
        setUser(safeUserData);
      }

      return safeUserData;
    } catch (error) {
      console.error("Register error:", error.response?.data || error.message);
      throw error;
    }
  };

  // ============================================
  // LOGOUT
  // ============================================
  const logout = async () => {
    try {
      await logoutAPI();
    } catch (error) {
      console.error("Logout API error:", error);
    }
    localStorage.removeItem("user");
    localStorage.removeItem("cart");
    setUser(null);
  };

  // ============================================
  // UPDATE USER
  // ============================================
  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const refreshUser = async () => {
    try {
      const { data } = await getMe();
      const updatedUser = { ...user, ...data };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        updateUser,
        refreshUser,
        loading,
        isRefreshing,
        isSuperAdmin,
        isAdmin,
        isTeamMember,
        isStaff,
        isFarmer,
        isCustomer,
        isSupplier,
        isPendingApproval,
        getTeamRoleLabel,
        getEffectiveRole,
        getEffectiveRoleLabel,
        getRoleIcon,
        getRoleColor,
        hasPermission,
        getDashboardTabs,
        getDashboardConfig,
        getDashboardStats,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};